/**
 * App coordinator — orchestrates editor + execution stores.
 *
 * No state is owned here; this is a plain action facade.
 */

import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { ISavedPipeline } from '@/interfaces/IStorage';
import { TOOL_TO_MODE } from '@/lib/visualizerManifest';
import { useEditorStore } from '@/store/useEditorStore';
import { useExecutionStore } from '@/store/useExecutionStore';
import { useTabStore } from '@/store/useTabStore';
import { useVisualizerStore, type VisualizerMode } from '@/store/useVisualizerStore';

type AppActions = {
  setInput: (data: string) => void;
  setInputType: (type: ISemanticType | null) => void;
  setInputTypeMode: (mode: 'auto' | 'manual') => void;
  setInputMimeType: (mimeType: string | null) => void;
  addTool: (toolId: string, position?: 'head' | 'tail', panelRole?: 'input' | 'output') => void;
  addToolWithConfig: (toolId: string, config: Record<string, unknown>, label?: string) => void;
  removeTool: (stepId: string) => void;
  updateToolConfig: (stepId: string, config: Record<string, unknown>) => void;
  toggleToolEnabled: (stepId: string) => void;
  reorderPipeline: (startIndex: number, endIndex: number) => void;
  clearPipeline: () => void;
  runPipeline: () => Promise<void>;
  rerunFromStep: (stepIndex: number, newInput: string, options?: { updateInput?: boolean }) => Promise<void>;
  run: () => Promise<void>;
  startStepThrough: () => void;
  stepThroughNext: () => Promise<void>;
  stopStepThrough: () => void;
  loadPipeline: (pipeline: ISavedPipeline) => void;
};

function restoreEditorFromSnapshot(snapshot: {
  input: string;
  output: string;
  inputType: ISemanticType | null;
  stepResults: { outputType?: ISemanticType | null }[];
}) {
  const lastOutputType = snapshot.stepResults.length > 0
    ? snapshot.stepResults[snapshot.stepResults.length - 1]?.outputType ?? null
    : null;
  useEditorStore.setState({
    input: snapshot.input,
    output: snapshot.output,
    inputType: snapshot.inputType,
    outputType: lastOutputType,
  });
}

function stopStepThroughIfActive() {
  const exec = useExecutionStore.getState();
  if (!exec.stepThrough?.active) return;
  const snapshot = exec.stopStepThrough();
  if (snapshot) restoreEditorFromSnapshot(snapshot);
}

function priorResultsMatchPipeline(
  pipeline: { id: string }[],
  stepResults: { stepId: string }[],
  stepIndex: number,
): boolean {
  if (stepIndex <= 0) return true;
  if (stepResults.length < stepIndex) return false;
  for (let i = 0; i < stepIndex; i++) {
    if (stepResults[i]?.stepId !== pipeline[i]?.id) return false;
  }
  return true;
}

function resetExecution(clearStepThrough = true) {
  useExecutionStore.getState().resetExecution({ clearStepThrough });
}

export const appActions: AppActions = {
  setInput: (data) => {
    useEditorStore.getState().setInput(data);
    resetExecution(true);
  },

  setInputType: (type) => {
    useEditorStore.getState().setInputType(type);
  },

  setInputTypeMode: (mode) => {
    useEditorStore.getState().setInputTypeMode(mode);
  },

  setInputMimeType: (mimeType) => {
    useEditorStore.getState().setInputMimeType(mimeType);
  },

  addTool: (toolId, position, panelRole) => {
    stopStepThroughIfActive();
    useEditorStore.getState().addTool(toolId, position, panelRole);
    resetExecution(true);
  },

  addToolWithConfig: (toolId, config, label) => {
    stopStepThroughIfActive();
    useEditorStore.getState().addToolWithConfig(toolId, config, label);
    resetExecution(true);
  },

  removeTool: (stepId) => {
    stopStepThroughIfActive();
    useEditorStore.getState().removeTool(stepId);
    resetExecution(true);
  },

  updateToolConfig: (stepId, config) => {
    stopStepThroughIfActive();
    useEditorStore.getState().updateToolConfig(stepId, config);
    resetExecution(true);
  },

  toggleToolEnabled: (stepId) => {
    stopStepThroughIfActive();
    useEditorStore.getState().toggleToolEnabled(stepId);
    resetExecution(true);
  },

  reorderPipeline: (startIndex, endIndex) => {
    stopStepThroughIfActive();
    useEditorStore.getState().reorderPipeline(startIndex, endIndex);
    resetExecution(true);
  },

  clearPipeline: () => {
    stopStepThroughIfActive();
    useEditorStore.getState().clearPipeline();
    resetExecution(true);
  },

  runPipeline: async () => {
    const editor = useEditorStore.getState();
    if (editor.pipeline.length === 0) return;

    const runTabId = useTabStore.getState().activeTabId;
    const isStale = () => useTabStore.getState().activeTabId !== runTabId;

    const result = await useExecutionStore.getState().runPipeline({
      input: editor.input,
      output: editor.output,
      inputType: editor.inputType,
      pipelineName: editor.activePipelineName ?? undefined,
      variables: editor.pipelineVariables,
      steps: editor.pipeline,
      isStale,
    });

    if (!result || isStale()) return;

    useEditorStore.getState().setOutput(result.output);
    useEditorStore.getState().setOutputType(result.outputType);
  },

  rerunFromStep: async (stepIndex, newInput, options) => {
    stopStepThroughIfActive();

    let editor = useEditorStore.getState();
    if (editor.pipeline.length === 0) return;
    if (stepIndex < 0 || stepIndex >= editor.pipeline.length) return;

    const runTabId = useTabStore.getState().activeTabId;
    const isStale = () => useTabStore.getState().activeTabId !== runTabId;

    if (isStale()) return;

    if (options?.updateInput) {
      useExecutionStore.getState().suppressNextAutoRun();
      useEditorStore.setState({ input: newInput });
    }

    const execState = useExecutionStore.getState();
    editor = useEditorStore.getState();
    const pipeline = useEditorStore.getState().pipeline;
    const priorResults = execState.stepResults;

    if (isStale()) return;

    if (!priorResultsMatchPipeline(pipeline, priorResults, stepIndex)) {
      if (stepIndex === 0 && options?.updateInput) {
        await appActions.runPipeline();
      }
      return;
    }

    const result = await useExecutionStore.getState().rerunFromStep({
      input: editor.input,
      output: editor.output,
      inputType: editor.inputType,
      pipelineName: editor.activePipelineName ?? undefined,
      variables: editor.pipelineVariables,
      steps: editor.pipeline,
      isStale,
    }, stepIndex, newInput, priorResults);

    if (!result || isStale()) return;

    useEditorStore.getState().setOutput(result.output);
    useEditorStore.getState().setOutputType(result.outputType);
  },

  run: async () => {
    await appActions.runPipeline();
  },

  startStepThrough: () => {
    const editor = useEditorStore.getState();
    if (editor.pipeline.length === 0) return;
    useExecutionStore.getState().startStepThrough({
      input: editor.input,
      output: editor.output,
      inputType: editor.inputType,
      pipelineName: editor.activePipelineName ?? undefined,
      variables: editor.pipelineVariables,
      steps: editor.pipeline,
    });
    useEditorStore.setState({ output: '', outputType: null });
  },

  stepThroughNext: async () => {
    const editor = useEditorStore.getState();
    const runTabId = useTabStore.getState().activeTabId;
    const isStale = () => useTabStore.getState().activeTabId !== runTabId;

    const result = await useExecutionStore.getState().stepThroughNext({
      pipelineName: editor.activePipelineName ?? undefined,
      variables: editor.pipelineVariables,
      steps: editor.pipeline,
      isStale,
    });

    if (!result || isStale()) return;

    useEditorStore.setState({
      input: result.nextInput,
      output: result.nextOutput,
      outputType: result.nextOutputType,
    });
  },

  stopStepThrough: () => {
    const snapshot = useExecutionStore.getState().stopStepThrough();
    if (snapshot) restoreEditorFromSnapshot(snapshot);
  },

  loadPipeline: (pipeline: ISavedPipeline) => {
    stopStepThroughIfActive();
    const reordered = useEditorStore.getState().loadPipeline(pipeline);

    const inputVizModes: VisualizerMode[] = ['raw'];
    const outputVizModes: VisualizerMode[] = ['raw'];

    for (let i = 0; i < reordered.length; i++) {
      const step = reordered[i];
      const mode = TOOL_TO_MODE[step.toolId];
      if (!mode) continue;

      // Explicit panelRole takes priority; otherwise infer from position
      let role = step.panelRole;
      if (!role) {
        const firstRealIdx = reordered.findIndex(s => !TOOL_TO_MODE[s.toolId]);
        role = (firstRealIdx === -1 || i < firstRealIdx) ? 'input' : 'output';
        step.panelRole = role;
      }

      if (role === 'input') inputVizModes.push(mode);
      else if (role === 'output') outputVizModes.push(mode);
    }

    const vizStore = useVisualizerStore.getState();
    useVisualizerStore.setState({ inputActiveModes: inputVizModes, outputActiveModes: outputVizModes });
    vizStore.setMode('input', inputVizModes[inputVizModes.length - 1]);
    vizStore.setMode('output', outputVizModes[outputVizModes.length - 1]);

    resetExecution(true);
  },
};

export const useAppStore = () => appActions;
